import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Plus,
  Copy,
  Check,
  ExternalLink,
  LogOut,
  Lock,
  RefreshCw,
  Eye,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Globe,
  GlobeLock,
  Search,
  FileDown,
  FileText,
  Link2,
  Trash,
  Zap,
  ShieldCheck,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";

import { loginPainel, logoutPainel, checkPainelAuth } from "@/lib/painel-auth.functions";
import {
  getAllLandingPages,
  createLandingPage,
  updateLandingPage,
  deleteLandingPage,
} from "@/lib/painel.functions";
import { refreshGscMetrics, inspectLandingPage, inspectAllLandingPages } from "@/lib/gsc.functions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAllMatrices,
  createMatrix,
  updateMatrix,
  deleteMatrix,
} from "@/lib/matrices.functions";
import { exportPagesToCsv, exportPagesToPdf, type ReportPage } from "@/lib/report-export";
import {
  listShareLinks,
  createShareLink,
  revokeShareLink,
  deleteShareLink,
  type ShareLink,
} from "@/lib/share-links.functions";

const BASE_URL = "https://lp.selectusrh.com";

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("pt-BR").format(n);
}

function KpiCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent: "navy" | "green" | "orange" | "blue";
}) {
  const colors: Record<string, string> = {
    navy: "bg-brand-navy",
    green: "bg-emerald-500",
    orange: "bg-brand-orange",
    blue: "bg-blue-500",
  };
  return (
    <div className="relative overflow-hidden rounded-xl border bg-white p-4 shadow-sm">
      <span className={`absolute left-0 top-0 h-full w-1 ${colors[accent]}`} />
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold text-brand-navy">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) return "há poucos minutos";
    return `há ${hours}h`;
  }
  if (days === 1) return "há 1 dia";
  return `há ${days} dias`;
}

function PositionBadge({
  position,
  impressions,
  updatedAt,
  published,
  previousPosition,
}: {
  position: number | null | undefined;
  impressions: number | null | undefined;
  updatedAt: string | null | undefined;
  published: boolean;
  previousPosition?: number | null;
}) {
  if (!published) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (position === null || position === undefined) {
    return (
      <span className="text-xs text-muted-foreground">
        {impressions === 0 ? "Sem impressões" : "Sem dados"}
      </span>
    );
  }
  const rounded = Math.round(position);
  let color = "bg-red-100 text-red-800 border-red-200";
  if (rounded <= 3) color = "bg-emerald-100 text-emerald-800 border-emerald-200";
  else if (rounded <= 10) color = "bg-yellow-100 text-yellow-800 border-yellow-200";
  else if (rounded <= 30) color = "bg-orange-100 text-orange-800 border-orange-200";

  // Delta: previous - current. Positive = subiu (posição menor é melhor).
  let deltaEl: React.ReactNode = null;
  if (previousPosition === null || previousPosition === undefined) {
    deltaEl = (
      <span className="rounded bg-blue-100 px-1 text-[10px] font-semibold text-blue-700">
        novo
      </span>
    );
  } else {
    const delta = previousPosition - position;
    const rounded = Math.round(delta * 10) / 10;
    if (Math.abs(rounded) < 0.1) {
      deltaEl = <span className="text-[10px] font-semibold text-muted-foreground">=</span>;
    } else if (rounded > 0) {
      deltaEl = (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700">
          <ArrowUp className="h-3 w-3" />+{rounded.toFixed(1)}
        </span>
      );
    } else {
      deltaEl = (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-700">
          <ArrowDown className="h-3 w-3" />
          {rounded.toFixed(1)}
        </span>
      );
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-flex w-fit items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${color}`}
        >
          #{position.toFixed(1)}
        </span>
        {deltaEl}
      </div>
      {updatedAt && (
        <span className="mt-0.5 text-[10px] text-muted-foreground">{timeAgo(updatedAt)}</span>
      )}
    </div>
  );
}

function IndexBadge({
  verdict,
  coverage,
  lastCrawl,
  checkedAt,
  loading,
  onRefresh,
}: {
  verdict: string | null | undefined;
  coverage: string | null | undefined;
  lastCrawl: string | null | undefined;
  checkedAt: string | null | undefined;
  loading: boolean;
  onRefresh: () => void;
}) {
  let label = "Não verificada";
  let color = "bg-slate-100 text-slate-700 border-slate-200";
  if (verdict === "PASS") {
    label = "Indexada";
    color = "bg-emerald-100 text-emerald-800 border-emerald-200";
  } else if (verdict === "PARTIAL") {
    label = "Com problemas";
    color = "bg-yellow-100 text-yellow-800 border-yellow-200";
  } else if (verdict === "FAIL" || verdict === "NEUTRAL") {
    label = "Não indexada";
    color = "bg-red-100 text-red-800 border-red-200";
  }
  const tooltip = [
    coverage ? `Cobertura: ${coverage}` : null,
    lastCrawl ? `Último crawl: ${new Date(lastCrawl).toLocaleString("pt-BR")}` : null,
    checkedAt ? `Verificado: ${new Date(checkedAt).toLocaleString("pt-BR")}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`inline-flex w-fit items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${color}`}
        title={tooltip || "Ainda não verificada"}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border text-muted-foreground transition hover:text-brand-navy disabled:opacity-50"
        title="Verificar agora"
      >
        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}

export const Route = createFileRoute("/painel")({
  head: () => ({
    meta: [
      { title: "Painel Selectus RH" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PainelPage,
});

function PainelPage() {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const authQuery = useQuery({
    queryKey: ["painel-auth"],
    queryFn: async () => checkPainelAuth(),
  });

  const isAuth = authQuery.data === true;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    try {
      const result = await loginPainel({ data: { password } });
      if (result.cookie) {
        document.cookie = result.cookie;
      }
      queryClient.invalidateQueries({ queryKey: ["painel-auth"] });
    } catch (err: any) {
      setLoginError(err.message || "Erro ao fazer login");
    }
  };

  const handleLogout = async () => {
    const result = await logoutPainel();
    if (result.cookie) {
      document.cookie = result.cookie;
    }
    queryClient.invalidateQueries({ queryKey: ["painel-auth"] });
  };

  if (!isAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <div className="w-full max-w-sm rounded-xl border bg-white p-8 shadow-sm">
          <div className="mb-6 text-center">
            <Lock className="mx-auto h-10 w-10 text-brand-navy" />
            <h1 className="mt-4 font-display text-xl font-bold text-brand-navy">
              Painel Selectus RH
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Digite a senha de acesso
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            {loginError && (
              <p className="text-sm text-red-600">{loginError}</p>
            )}
            <Button type="submit" className="w-full">
              Entrar
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return <PainelDashboard onLogout={handleLogout} />;
}

function PainelDashboard({ onLogout }: { onLogout: () => void }) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [matrixFilter, setMatrixFilter] = useState<string>("all");

  type SortKey =
    | "slug"
    | "title"
    | "cidade"
    | "gsc_position"
    | "gsc_impressions"
    | "gsc_clicks"
    | "published";
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }, []);

  const [form, setForm] = useState({
    slug: "",
    title: "",
    meta_description: "",
    h1: "",
    cidade: "",
    bairro: "",
    preposicao_cidade: "em",
    preposicao_bairro: "em",
    texto_hero: "",
    texto_beneficios: "",
    palavra_chave: "",
    published: false,
    matrix_id: "" as string,
  });

  const pagesQuery = useQuery({
    queryKey: ["landing-pages-all"],
    queryFn: async () => getAllLandingPages(),
    enabled: true,
  });

  const matricesQuery = useQuery({
    queryKey: ["matrices"],
    queryFn: async () => getAllMatrices(),
  });
  const matrices = matricesQuery.data ?? [];

  const [matricesDialogOpen, setMatricesDialogOpen] = useState(false);
  const [shareLinksDialogOpen, setShareLinksDialogOpen] = useState(false);

  const createMutation = useMutation({
    mutationFn: createLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      setConfirmDelete(null);
    },
  });

  const refreshGscMutation = useMutation({
    mutationFn: () => refreshGscMetrics(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
    },
  });

  const inspectAllMutation = useMutation({
    mutationFn: () => inspectAllLandingPages(),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      toast.success(
        `${res?.updated ?? 0} página(s) verificadas${
          res?.skipped ? ` · ${res.skipped} com erro` : ""
        }`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Erro ao consultar indexação");
    },
  });

  const inspectOneMutation = useMutation({
    mutationFn: (slug: string) => inspectLandingPage({ data: { slug } }),
    onSuccess: (_res, slug) => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      toast.success(`Indexação atualizada para ${slug}`);
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Erro ao consultar indexação");
    },
  });

  const togglePublishMutation = useMutation({
    mutationFn: (page: any) =>
      updateLandingPage({
        data: {
          id: page.id,
          slug: page.slug,
          title: page.title,
          meta_description: page.meta_description ?? null,
          h1: page.h1 ?? null,
          cidade: page.cidade ?? null,
          bairro: page.bairro ?? null,
          preposicao_cidade: page.preposicao_cidade ?? "em",
          preposicao_bairro: page.preposicao_bairro ?? "em",
          texto_hero: page.texto_hero ?? null,
          texto_beneficios: page.texto_beneficios ?? null,
          palavra_chave: page.palavra_chave ?? null,
          published: !page.published,
          matrix_id: page.matrix_id ?? null,
        } as any,
      }),
    onSuccess: (_data, page: any) => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages-all"] });
      toast.success(page.published ? "Página despublicada" : "Página publicada");
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Erro ao alterar status");
    },
  });

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm({
      slug: "",
      title: "",
      meta_description: "",
      h1: "",
      cidade: "",
      bairro: "",
      preposicao_cidade: "em",
      preposicao_bairro: "em",
      texto_hero: "",
      texto_beneficios: "",
      palavra_chave: "",
      published: false,
      matrix_id: matrices[0]?.id ?? "",
    });
    setDialogOpen(true);
  }, [matrices]);

  const openEdit = useCallback(
    (page: any) => {
      setEditingId(page.id);
      setForm({
        slug: page.slug ?? "",
        title: page.title ?? "",
        meta_description: page.meta_description ?? "",
        h1: page.h1 ?? "",
        cidade: page.cidade ?? "",
        bairro: page.bairro ?? "",
        preposicao_cidade: page.preposicao_cidade ?? "em",
        preposicao_bairro: page.preposicao_bairro ?? "em",
        texto_hero: page.texto_hero ?? "",
        texto_beneficios: page.texto_beneficios ?? "",
        palavra_chave: page.palavra_chave ?? "",
        published: page.published ?? false,
        matrix_id: page.matrix_id ?? "",
      });
      setDialogOpen(true);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingId(null);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      matrix_id: form.matrix_id ? form.matrix_id : null,
    };
    if (editingId) {
      updateMutation.mutate({
        data: { ...payload, id: editingId } as any,
      });
    } else {
      createMutation.mutate({ data: payload as any });
    }
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`${BASE_URL}/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const pages = pagesQuery.data ?? [];

  const filteredPages = useMemo(() => {
    const norm = (s: string) =>
      s
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();
    const q = norm(search.trim());
    return pages.filter((p: any) => {
      if (matrixFilter !== "all" && p.matrix_id !== matrixFilter) return false;
      if (!q) return true;
      const hay = norm(
        [p.slug, p.title, p.palavra_chave, p.cidade, p.bairro, p.matrix?.nome]
          .filter(Boolean)
          .join(" "),
      );
      return hay.includes(q);
    });
  }, [pages, search, matrixFilter]);

  const stats = useMemo(() => {
    const total = pages.length;
    const published = pages.filter((p: any) => p.published).length;
    const draft = total - published;
    const top10 = pages.filter(
      (p: any) => p.published && p.gsc_position != null && p.gsc_position <= 10,
    ).length;
    const lastUpdate = pages
      .map((p: any) => p.gsc_updated_at)
      .filter(Boolean)
      .sort()
      .reverse()[0] as string | undefined;
    return { total, published, draft, top10, lastUpdate };
  }, [pages]);

  const otherSlugs = useMemo(
    () =>
      new Set(
        pages
          .filter((p: any) => p.id !== editingId)
          .map((p: any) => (p.slug ?? "").toLowerCase()),
      ),
    [pages, editingId],
  );
  const slugConflict =
    form.slug.trim().length > 0 && otherSlugs.has(form.slug.trim().toLowerCase());

  const toReportPages = (rows: any[]): ReportPage[] =>
    rows.map((p) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      palavra_chave: p.palavra_chave,
      cidade: p.cidade,
      bairro: p.bairro,
      gsc_position: p.gsc_position,
      gsc_previous_position: p.gsc_previous_position,
      gsc_impressions: p.gsc_impressions,
      gsc_clicks: p.gsc_clicks,
      gsc_updated_at: p.gsc_updated_at,
      published: p.published,
      matrix: p.matrix,
    }));

  const sortedPages = (() => {
    if (!sort) return filteredPages;
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    const getVal = (p: any): { v: any; empty: boolean } => {
      switch (key) {
        case "slug":
          return { v: (p.slug ?? "").toLowerCase(), empty: !p.slug };
        case "title":
          return { v: (p.title ?? "").toLowerCase(), empty: !p.title };
        case "cidade":
          return { v: (p.cidade ?? "").toLowerCase(), empty: !p.cidade };
        case "gsc_position":
          return { v: p.gsc_position ?? Infinity, empty: p.gsc_position == null };
        case "gsc_impressions":
          return { v: p.gsc_impressions ?? 0, empty: p.gsc_impressions == null };
        case "gsc_clicks":
          return { v: p.gsc_clicks ?? 0, empty: p.gsc_clicks == null };
        case "published":
          return { v: p.published ? 1 : 0, empty: false };
      }
    };
    return [...filteredPages].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      // Empty values always go to the end
      if (av.empty && !bv.empty) return 1;
      if (!av.empty && bv.empty) return -1;
      if (av.empty && bv.empty) return 0;
      if (av.v < bv.v) return -1 * dir;
      if (av.v > bv.v) return 1 * dir;
      return 0;
    });
  })();

  // Pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(sortedPages.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedPages = sortedPages.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setPage(1);
  }, [search, matrixFilter, sort]);

  const SortHeader = ({
    label,
    sortKey,
    align = "left",
  }: {
    label: string;
    sortKey: SortKey;
    align?: "left" | "right";
  }) => {
    const active = sort?.key === sortKey;
    const Icon = !active ? ArrowUpDown : sort!.dir === "asc" ? ArrowUp : ArrowDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-brand-navy ${
          active ? "text-brand-navy font-semibold" : ""
        } ${align === "right" ? "ml-auto" : ""}`}
      >
        {label}
        <Icon className="h-3 w-3 opacity-60" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="font-display text-xl font-bold text-brand-navy">
              Painel de Landing Pages
            </h1>
            <p className="text-sm text-muted-foreground">
              Gerencie todas as páginas de uma só vez
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-muted-foreground hover:text-brand-navy">
              Ver site →
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareLinksDialogOpen(true)}
            >
              <Link2 className="mr-1 h-4 w-4" />
              Links compartilháveis
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOut className="mr-1 h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Total de páginas"
            value={String(stats.total)}
            sub={`${stats.published} publicadas · ${stats.draft} rascunhos`}
            accent="navy"
          />
          <KpiCard
            label="Publicadas"
            value={String(stats.published)}
            sub={`${((stats.published / Math.max(stats.total, 1)) * 100).toFixed(0)}% do total`}
            accent="green"
          />
          <KpiCard
            label="Top 10 Google"
            value={String(stats.top10)}
            sub="posição ≤ 10"
            accent="orange"
          />
          <KpiCard
            label="Última atualização"
            value={stats.lastUpdate ? timeAgo(stats.lastUpdate) : "—"}
            sub={stats.lastUpdate ? "GSC" : "sem consulta"}
            accent="blue"
          />
        </div>

        {/* Single-line toolbar */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar slug, título, cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
          <Select value={matrixFilter} onValueChange={setMatrixFilter}>
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder="Todas as matrizes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as matrizes</SelectItem>
              {matrices.map((m: any) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sortedPages.length === 0}
                >
                  <FileDown className="mr-1 h-4 w-4" />
                  Exportar
                  <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => exportPagesToCsv(toReportPages(sortedPages))}
                >
                  <FileDown className="mr-2 h-4 w-4" /> CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    exportPagesToPdf(toReportPages(sortedPages), {
                      subtitle:
                        search || matrixFilter !== "all"
                          ? `Relatório filtrado (${sortedPages.length} de ${pages.length})`
                          : undefined,
                    })
                  }
                >
                  <FileText className="mr-2 h-4 w-4" /> PDF
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShareLinksDialogOpen(true)}>
                  <Link2 className="mr-2 h-4 w-4" /> Links compartilháveis
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={refreshGscMutation.isPending || inspectAllMutation.isPending}
                  title={
                    refreshGscMutation.data
                      ? `${refreshGscMutation.data.updated} página(s) atualizadas`
                      : "Atualizar dados do Search Console"
                  }
                >
                  <RefreshCw
                    className={`mr-1 h-4 w-4 ${
                      refreshGscMutation.isPending || inspectAllMutation.isPending
                        ? "animate-spin"
                        : ""
                    }`}
                  />
                  Atualizar
                  <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => refreshGscMutation.mutate()}
                  disabled={refreshGscMutation.isPending}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Posições (GSC)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => inspectAllMutation.mutate()}
                  disabled={inspectAllMutation.isPending}
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Indexação
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setMatricesDialogOpen(true)}
            >
              Matrizes ({matrices.length})
            </Button>
            <Button size="sm" onClick={openCreate} className="btn-orange btn-orange-hover">
              <Plus className="mr-1 h-4 w-4" />
              Nova página
            </Button>
          </div>
        </div>

        <div className="rounded-xl border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortHeader label="Slug" sortKey="slug" /></TableHead>
                <TableHead>Matriz</TableHead>
                <TableHead><SortHeader label="Cidade / Bairro" sortKey="cidade" /></TableHead>
                <TableHead><SortHeader label="Posição Google" sortKey="gsc_position" /></TableHead>
                <TableHead>Indexação</TableHead>
                <TableHead className="text-right"><SortHeader label="Impressões" sortKey="gsc_impressions" align="right" /></TableHead>
                <TableHead className="text-right"><SortHeader label="Cliques" sortKey="gsc_clicks" align="right" /></TableHead>
                <TableHead><SortHeader label="Status" sortKey="published" /></TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPages.map((page: any) => (
                <TableRow key={page.id} className="[&>td]:py-2">
                  <TableCell className="max-w-[220px] truncate font-medium" title={page.slug}>
                    {page.slug}
                  </TableCell>
                  <TableCell className="text-xs">
                    {page.matrix?.nome ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {page.cidade ?? "—"}
                    {page.bairro ? (
                      <span className="block text-xs text-muted-foreground">{page.bairro}</span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <PositionBadge
                      position={page.gsc_position}
                      previousPosition={page.gsc_previous_position}
                      impressions={page.gsc_impressions}
                      updatedAt={page.gsc_updated_at}
                      published={page.published}
                    />
                  </TableCell>
                  <TableCell>
                    <IndexBadge
                      verdict={page.gsc_index_verdict}
                      coverage={page.gsc_index_coverage}
                      lastCrawl={page.gsc_last_crawl}
                      checkedAt={page.gsc_index_checked_at}
                      loading={
                        inspectOneMutation.isPending &&
                        inspectOneMutation.variables === page.slug
                      }
                      onRefresh={() => inspectOneMutation.mutate(page.slug)}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(page.gsc_impressions)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(page.gsc_clicks)}
                  </TableCell>
                  <TableCell>
                    {page.published ? (
                      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                        Publicado
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Rascunho</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => copyUrl(page.slug)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-brand-navy"
                        title="Copiar URL"
                      >
                        {copiedSlug === page.slug ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <a
                        href={`${BASE_URL}/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-brand-navy"
                        title="Abrir página"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <a
                        href={`/preview/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-amber-600"
                        title="Preview (mesmo em rascunho)"
                      >
                        <Eye className="h-4 w-4" />
                      </a>
                      <a
                        href={`https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(
                          "sc-domain:lp.selectusrh.com",
                        )}&id=${encodeURIComponent(`${BASE_URL}/${page.slug}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-amber-500"
                        title="Solicitar indexação no Google (abre o Search Console)"
                      >
                        <Zap className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => togglePublishMutation.mutate(page)}
                        disabled={togglePublishMutation.isPending}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition ${
                          page.published
                            ? "text-emerald-600 hover:text-red-600"
                            : "text-muted-foreground hover:text-emerald-600"
                        }`}
                        title={page.published ? "Despublicar" : "Publicar"}
                      >
                        {page.published ? (
                          <GlobeLock className="h-4 w-4" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEdit(page)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-brand-navy"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(page.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition hover:text-red-600"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {pages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    Nenhuma landing page cadastrada ainda.
                  </TableCell>
                </TableRow>
              )}
              {pages.length > 0 && sortedPages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    Nenhum resultado para o filtro atual.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {sortedPages.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
              <span>
                Mostrando{" "}
                <span className="font-medium text-foreground">
                  {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, sortedPages.length)}
                </span>{" "}
                de <span className="font-medium text-foreground">{sortedPages.length}</span>
              </span>
              {totalPages > 1 && (
                <Pagination className="mx-0 w-auto justify-end">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        aria-disabled={currentPage === 1}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((p) => Math.max(1, p - 1));
                        }}
                      >
                        ‹
                      </PaginationLink>
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (n) =>
                          n === 1 ||
                          n === totalPages ||
                          Math.abs(n - currentPage) <= 1,
                      )
                      .reduce<(number | "…")[]>((acc, n) => {
                        const prev = acc[acc.length - 1];
                        if (typeof prev === "number" && n - prev > 1) acc.push("…");
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((n, idx) =>
                        n === "…" ? (
                          <PaginationItem key={`e${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={n}>
                            <PaginationLink
                              href="#"
                              isActive={n === currentPage}
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(n);
                              }}
                            >
                              {n}
                            </PaginationLink>
                          </PaginationItem>
                        ),
                      )}
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        aria-disabled={currentPage === totalPages}
                        className={
                          currentPage === totalPages ? "pointer-events-none opacity-50" : ""
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((p) => Math.min(totalPages, p + 1));
                        }}
                      >
                        ›
                      </PaginationLink>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar Landing Page" : "Nova Landing Page"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="matrix">Matriz de palavra-chave*</Label>
              <Select
                value={form.matrix_id}
                onValueChange={(v) => {
                  const m = matrices.find((x: any) => x.id === v);
                  setForm({
                    ...form,
                    matrix_id: v,
                    // pre-fill templates when creating & empty
                    texto_hero:
                      form.texto_hero || !m?.template_hero
                        ? form.texto_hero
                        : m.template_hero,
                    texto_beneficios:
                      form.texto_beneficios || !m?.template_beneficios
                        ? form.texto_beneficios
                        : m.template_beneficios,
                  });
                }}
              >
                <SelectTrigger id="matrix">
                  <SelectValue placeholder="Selecione uma matriz" />
                </SelectTrigger>
                <SelectContent>
                  {matrices.map((m: any) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Define a palavra-chave-mãe e os textos padrão (hero e benefícios).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="palavra_chave">Palavra-chave alvo*</Label>
              <Input
                id="palavra_chave"
                value={form.palavra_chave}
                onChange={(e) => setForm({ ...form, palavra_chave: e.target.value })}
                placeholder="Mão de Obra Temporária para Construção Civil {no_bairro}"
              />
              <p className="text-xs text-muted-foreground">
                Use {"{cidade}"} / {"{bairro}"} (só o nome) ou {"{no_cidade}"} / {"{no_bairro}"} (preposição + nome, ex.: "no ABC", "na Paulista", "nos Jardins"). O sistema substitui automaticamente no hero, seção "Especialistas", bloco SEO e footer.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug (URL)*</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const normalized = raw
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/\p{Diacritic}/gu, "")
                      .replace(/[^a-z0-9-\s]/g, "")
                      .replace(/\s+/g, "-")
                      .replace(/-+/g, "-");
                    setForm({ ...form, slug: normalized });
                  }}
                  placeholder="mao-de-obra-temporaria-construcao-civil-em-pinheiros"
                  required
                  className={
                    slugConflict
                      ? "border-red-500 focus-visible:ring-red-500"
                      : form.slug
                        ? "border-emerald-500 focus-visible:ring-emerald-500"
                        : ""
                  }
                />
                {slugConflict ? (
                  <p className="text-xs font-medium text-red-600">
                    Slug já em uso por outra página
                  </p>
                ) : form.slug ? (
                  <p className="text-xs text-emerald-700">
                    URL disponível: {BASE_URL}/{form.slug}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Será acessível em /{"<slug>"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Título (SEO)*</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Mão de obra temporária para construção civil em Pinheiros"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta_description">Meta description</Label>
              <Textarea
                id="meta_description"
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                placeholder="Descrição para Google (até 160 caracteres)"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="h1">H1</Label>
              <Input
                id="h1"
                value={form.h1}
                onChange={(e) => setForm({ ...form, h1: e.target.value })}
                placeholder="Mão de obra temporária para construção civil em Pinheiros"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  value={form.bairro}
                  onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                  placeholder="Pinheiros"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={form.cidade}
                  onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                  placeholder="São Paulo"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="preposicao_bairro">Preposição do bairro</Label>
                <Select
                  value={form.preposicao_bairro}
                  onValueChange={(v) => setForm({ ...form, preposicao_bairro: v })}
                >
                  <SelectTrigger id="preposicao_bairro">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="em">em (ex.: em Pinheiros)</SelectItem>
                    <SelectItem value="no">no (ex.: no ABC)</SelectItem>
                    <SelectItem value="na">na (ex.: na Paulista)</SelectItem>
                    <SelectItem value="nos">nos (ex.: nos Jardins)</SelectItem>
                    <SelectItem value="nas">nas (ex.: nas Perdizes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="preposicao_cidade">Preposição da cidade</Label>
                <Select
                  value={form.preposicao_cidade}
                  onValueChange={(v) => setForm({ ...form, preposicao_cidade: v })}
                >
                  <SelectTrigger id="preposicao_cidade">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="em">em (ex.: em São Paulo)</SelectItem>
                    <SelectItem value="no">no (ex.: no Rio)</SelectItem>
                    <SelectItem value="na">na (ex.: na Barra)</SelectItem>
                    <SelectItem value="nos">nos</SelectItem>
                    <SelectItem value="nas">nas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="texto_hero">Texto do Hero</Label>
              <p className="text-xs text-muted-foreground">
                Use {"{cidade}"} e {"{bairro}"} como variáveis
              </p>
              <Textarea
                id="texto_hero"
                value={form.texto_hero}
                onChange={(e) => setForm({ ...form, texto_hero: e.target.value })}
                placeholder="A Selectus RH mobiliza equipes qualificadas..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="texto_beneficios">Texto de Benefícios</Label>
              <p className="text-xs text-muted-foreground">
                Use {"{cidade}"} e {"{bairro}"} como variáveis
              </p>
              <Textarea
                id="texto_beneficios"
                value={form.texto_beneficios}
                onChange={(e) => setForm({ ...form, texto_beneficios: e.target.value })}
                placeholder="Recrutamento especializado em mão de obra temporária..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch
                id="published"
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
              />
              <Label htmlFor="published" className="cursor-pointer">
                Publicado
              </Label>
            </div>

            {(createMutation.isError || updateMutation.isError) && (
              <p className="text-sm text-red-600">
                {((createMutation.error as any)?.message ||
                  (updateMutation.error as any)?.message) ??
                  "Erro ao salvar"}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending || slugConflict}
                className="btn-orange btn-orange-hover"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Salvando..."
                  : editingId
                    ? "Salvar alterações"
                    : "Criar página"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Tem certeza que deseja excluir esta landing page? Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDelete) {
                  deleteMutation.mutate({ data: { id: confirmDelete } });
                }
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MatricesDialog
        open={matricesDialogOpen}
        onOpenChange={setMatricesDialogOpen}
        matrices={matrices}
      />

      <ShareLinksDialog
        open={shareLinksDialogOpen}
        onOpenChange={setShareLinksDialogOpen}
      />
    </div>
  );
}

function MatricesDialog({
  open,
  onOpenChange,
  matrices,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  matrices: any[];
}) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    nome: "",
    palavra_chave_base: "",
    segmento: "",
    template_hero: "",
    template_beneficios: "",
  });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const createMut = useMutation({
    mutationFn: createMatrix,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matrices"] });
      resetForm();
    },
  });
  const updateMut = useMutation({
    mutationFn: updateMatrix,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matrices"] });
      resetForm();
    },
  });
  const deleteMut = useMutation({
    mutationFn: deleteMatrix,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matrices"] });
      setConfirmDeleteId(null);
    },
  });

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setForm({
      nome: "",
      palavra_chave_base: "",
      segmento: "",
      template_hero: "",
      template_beneficios: "",
    });
  };

  const openEdit = (m: any) => {
    setEditingId(m.id);
    setForm({
      nome: m.nome ?? "",
      palavra_chave_base: m.palavra_chave_base ?? "",
      segmento: m.segmento ?? "",
      template_hero: m.template_hero ?? "",
      template_beneficios: m.template_beneficios ?? "",
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMut.mutate({ data: { ...form, id: editingId } as any });
    } else {
      createMut.mutate({ data: form as any });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) resetForm();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Matrizes de palavra-chave</DialogTitle>
        </DialogHeader>

        {!showForm ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Cada matriz agrupa páginas de cauda longa de uma mesma palavra-chave-mãe.
              </p>
              <Button size="sm" onClick={() => setShowForm(true)} className="btn-orange btn-orange-hover">
                <Plus className="mr-1 h-4 w-4" />
                Nova matriz
              </Button>
            </div>

            <div className="rounded-lg border">
              {matrices.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nenhuma matriz cadastrada.
                </p>
              )}
              {matrices.map((m: any) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between border-b p-3 last:border-b-0"
                >
                  <div>
                    <div className="text-sm font-semibold text-brand-navy">{m.nome}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.palavra_chave_base}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/painel/matriz/$id/editar"
                      params={{ id: m.id }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-brand-orange bg-brand-orange px-2.5 text-xs font-semibold text-white hover:opacity-90"
                      title="Editor visual"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Editor visual
                    </Link>
                    <button
                      onClick={() => openEdit(m)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:text-brand-navy"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(m.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:text-red-600"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="m-nome">Nome*</Label>
              <Input
                id="m-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Construção Civil"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-kw">Palavra-chave base*</Label>
              <Input
                id="m-kw"
                value={form.palavra_chave_base}
                onChange={(e) => setForm({ ...form, palavra_chave_base: e.target.value })}
                placeholder="mão de obra temporária para construção civil"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-seg">Segmento*</Label>
              <Input
                id="m-seg"
                value={form.segmento}
                onChange={(e) => setForm({ ...form, segmento: e.target.value })}
                placeholder="Construção Civil"
                required
              />
              <p className="text-xs text-muted-foreground">
                Aparece nos textos via variável {"{segmento}"}.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-hero">Template do Hero</Label>
              <Textarea
                id="m-hero"
                value={form.template_hero}
                onChange={(e) => setForm({ ...form, template_hero: e.target.value })}
                rows={3}
                placeholder="Use {cidade}, {bairro} e {segmento}"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-ben">Template de Benefícios</Label>
              <Textarea
                id="m-ben"
                value={form.template_beneficios}
                onChange={(e) => setForm({ ...form, template_beneficios: e.target.value })}
                rows={3}
                placeholder="Use {cidade}, {bairro} e {segmento}"
              />
            </div>
            {(createMut.isError || updateMut.isError) && (
              <p className="text-sm text-red-600">
                {((createMut.error as any)?.message ||
                  (updateMut.error as any)?.message) ??
                  "Erro ao salvar"}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="btn-orange btn-orange-hover"
              >
                {createMut.isPending || updateMut.isPending
                  ? "Salvando..."
                  : editingId
                    ? "Salvar alterações"
                    : "Criar matriz"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {confirmDeleteId && (
          <Dialog open={true} onOpenChange={() => setConfirmDeleteId(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Excluir matriz?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Só é possível excluir matrizes que não estão em uso por nenhuma página.
              </p>
              {deleteMut.isError && (
                <p className="text-sm text-red-600">
                  {(deleteMut.error as any)?.message}
                </p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    deleteMut.mutate({ data: { id: confirmDeleteId } })
                  }
                  disabled={deleteMut.isPending}
                >
                  {deleteMut.isPending ? "Excluindo..." : "Excluir"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareLinksDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const linksQuery = useQuery({
    queryKey: ["share-links"],
    queryFn: async () => listShareLinks(),
    enabled: open,
  });
  const links = linksQuery.data ?? [];

  const createMut = useMutation({
    mutationFn: createShareLink,
    onSuccess: () => {
      setLabel("");
      queryClient.invalidateQueries({ queryKey: ["share-links"] });
      toast.success("Link criado");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao criar link"),
  });

  const revokeMut = useMutation({
    mutationFn: revokeShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["share-links"] });
      setConfirmRevokeId(null);
      toast.success("Link revogado");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao revogar"),
  });

  const deleteMut = useMutation({
    mutationFn: deleteShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["share-links"] });
      toast.success("Link excluído");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao excluir"),
  });

  const linkUrl = (token: string) => `${BASE_URL}/r/${token}`;

  const copyUrl = (id: string, token: string) => {
    navigator.clipboard.writeText(linkUrl(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    createMut.mutate({ data: { label: label.trim() } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Links compartilháveis</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleCreate} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="share-label">Nome do link (ex: Cliente X)</Label>
            <Input
              id="share-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Relatório Cliente X"
              className="mt-1"
            />
          </div>
          <Button
            type="submit"
            disabled={createMut.isPending || !label.trim()}
            className="btn-orange btn-orange-hover"
          >
            <Plus className="mr-1 h-4 w-4" />
            {createMut.isPending ? "Criando..." : "Criar link"}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground">
          Qualquer pessoa com o link poderá ver o relatório (somente páginas publicadas).
          Revogue o acesso a qualquer momento.
        </p>

        <div className="max-h-[50vh] overflow-y-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="text-right">Acessos</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((l: ShareLink) => (
                <TableRow key={l.id} className={l.revoked ? "opacity-60" : ""}>
                  <TableCell className="font-medium">{l.label}</TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate text-xs text-muted-foreground">
                      {linkUrl(l.token)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{l.access_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {l.last_accessed_at
                      ? new Date(l.last_accessed_at).toLocaleString("pt-BR")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {l.revoked ? (
                      <Badge variant="secondary">Revogado</Badge>
                    ) : (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600">Ativo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {!l.revoked && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyUrl(l.id, l.token)}
                            title="Copiar link"
                          >
                            {copiedId === l.id ? (
                              <Check className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(linkUrl(l.token), "_blank")}
                            title="Abrir"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmRevokeId(l.id)}
                            title="Revogar"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm(`Excluir permanentemente o link "${l.label}"?`)) {
                            deleteMut.mutate({ data: { id: l.id } });
                          }
                        }}
                        title="Excluir"
                      >
                        <Trash className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {links.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                    Nenhum link criado ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {confirmRevokeId && (
          <Dialog open onOpenChange={() => setConfirmRevokeId(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Revogar link?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                O relatório deixará de abrir para quem já tem o link.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmRevokeId(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => revokeMut.mutate({ data: { id: confirmRevokeId } })}
                  disabled={revokeMut.isPending}
                >
                  {revokeMut.isPending ? "Revogando..." : "Revogar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
